import { useState, type FormEvent } from 'react';
import { useLoaderData, useNavigate, useRevalidator, type LoaderFunctionArgs } from 'react-router-dom';
import { Button, Checkbox, Label, Modal, NativeSelect } from '@immo/ui-kit';
import type {
  AdminOrganizationListItem,
  ErrorCode,
  GetAdminMemberResponse,
  OrgRoleSlug,
  WorkspaceRoleSlug,
} from '@immo/api-contract';
import { useTranslations } from '@/i18n/use-translations';
import { apiClient } from '@/lib/auth/api-client';
import { ALL_WORKSPACES_QUERY } from '@/lib/test-workspaces/include-test-workspaces';

type Field = 'workspace-role' | 'organizations' | 'primary';

type FormErrors = Readonly<Record<Field, string | null>>;

type MemberAccessData = {
  readonly member: GetAdminMemberResponse;
  // null: the Standorte read failed — distinct from a workspace with none (BL-058).
  readonly organizations: ReadonlyArray<AdminOrganizationListItem> | null;
};

const NO_ERRORS: FormErrors = { 'workspace-role': null, organizations: null, primary: null };

// Highest first: a member holding several opens on the strongest.
const WORKSPACE_ROLES: ReadonlyArray<WorkspaceRoleSlug> = ['workspace-owner', 'workspace-admin', 'billing'];
const ORG_ROLES: ReadonlyArray<OrgRoleSlug> = ['organisation-owner', 'agent', 'assistant', 'viewer'];

const RETAIN_OWNER: ErrorCode = 'identity.member.workspace-must-retain-owner';
const AFFILIATION_REQUIRED: ErrorCode = 'identity.member.affiliation-required';
const AFFILIATION_REQUIRES_ROLE: ErrorCode = 'identity.member.affiliation-requires-role';
const ORGANIZATION_NOT_FOUND: ErrorCode = 'identity.organization.not-found';
const PRIMARY_NOT_IN_AFFILIATION: ErrorCode = 'identity.member.primary-not-in-affiliation';

type ErrorTarget = { readonly field: Field | 'form'; readonly key: string };

// The code is matched before the status: the last-owner refusal is a 403 with a code.
function errorTarget(code: string | undefined, status: number): ErrorTarget {
  if (code === RETAIN_OWNER) return { field: 'workspace-role', key: 'errors.workspace-must-retain-owner' };

  if (code === AFFILIATION_REQUIRED) return { field: 'organizations', key: 'errors.affiliation-required' };

  if (code === AFFILIATION_REQUIRES_ROLE) return { field: 'organizations', key: 'errors.affiliation-requires-role' };

  if (code === ORGANIZATION_NOT_FOUND) return { field: 'organizations', key: 'errors.organization-not-found' };

  if (code === PRIMARY_NOT_IN_AFFILIATION) return { field: 'primary', key: 'errors.primary-not-in-affiliation' };

  return { field: 'form', key: status === 403 && code === undefined ? 'errors.forbidden' : 'errors.generic' };
}

const highestWorkspaceRole = (held: ReadonlyArray<WorkspaceRoleSlug>): WorkspaceRoleSlug | '' =>
  WORKSPACE_ROLES.find((role) => held.includes(role)) ?? '';

export async function memberAccessLoader({ params }: LoaderFunctionArgs): Promise<MemberAccessData> {
  const id = params['id'];

  if (typeof id !== 'string' || id.length === 0) {
    throw new Response('Missing member id', { status: 404 });
  }

  const result = await apiClient.adminMembers.get({ pathParams: { id } });

  if (!result.ok) {
    throw new Response('Failed to load member', { status: result.status });
  }

  const member = result.body;
  // Supplementary: a failed read must not blank the route. The workspace comes
  // from the member's own record (BL-130).
  const organizations = await apiClient.adminOrganizations
    .list({ query: { workspaceId: member.workspaceId, ...ALL_WORKSPACES_QUERY } })
    .catch(() => null);

  return { member, organizations: organizations?.ok ? organizations.body.data : null };
}

export function MemberAccessPage() {
  const t = useTranslations('pages.admin.members.access');
  const tRoles = useTranslations('pages.admin.members.roles');
  const { member, organizations } = useLoaderData() as MemberAccessData;
  const navigate = useNavigate();
  const revalidator = useRevalidator();
  const [workspaceRole, setWorkspaceRole] = useState<WorkspaceRoleSlug | ''>(
    highestWorkspaceRole(member.workspaceRoles),
  );
  const [rolesByStandort, setRolesByStandort] = useState<ReadonlyMap<string, ReadonlyArray<OrgRoleSlug>>>(
    () => new Map(member.organizations.map((entry) => [entry.organizationId, entry.roles])),
  );
  const [primaryPick, setPrimaryPick] = useState<string | null>(null);
  const [errors, setErrors] = useState<FormErrors>(NO_ERRORS);
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const assigned = (organizations ?? []).filter((o) => (rolesByStandort.get(o.id)?.length ?? 0) > 0);
  const storedPrimary = member.organizations.find((o) => o.isPrimary)?.organizationId ?? null;
  const isAssigned = (id: string | null) => id !== null && assigned.some((o) => o.id === id);
  // Derived at render, never stored (BL-068): unticking the primary's last role
  // must move it.
  const primary = isAssigned(primaryPick)
    ? primaryPick
    : isAssigned(storedPrimary)
      ? storedPrimary
      : assigned.length === 1
        ? assigned[0]!.id
        : null;

  const clearError = (field: Field) => setErrors((prev) => (prev[field] === null ? prev : { ...prev, [field]: null }));

  const close = () => navigate(`/workspaces/members/${member.id}`);

  const toggleRole = (organizationId: string, role: OrgRoleSlug) => {
    setRolesByStandort((prev) => {
      const held = prev.get(organizationId) ?? [];
      const next = held.includes(role) ? held.filter((r) => r !== role) : [...held, role];

      return new Map(prev).set(
        organizationId,
        ORG_ROLES.filter((r) => next.includes(r)),
      );
    });
    clearError('organizations');
    clearError('primary');
  };

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormError(null);

    if (assigned.length >= 2 && primary === null) {
      setErrors({ ...NO_ERRORS, primary: t('errors.primary-not-in-affiliation') });

      return;
    }

    setErrors(NO_ERRORS);
    setSubmitting(true);

    try {
      const result = await apiClient.adminMembers.updateAccess({
        pathParams: { id: member.id },
        body: {
          workspaceRole: workspaceRole === '' ? null : workspaceRole,
          organizations: assigned.map((o) => ({ organizationId: o.id, roles: [...rolesByStandort.get(o.id)!] })),
          primaryOrganizationId: primary,
        },
      });

      if (result.ok) {
        await revalidator.revalidate();
        navigate(`/workspaces/members/${member.id}`);

        return;
      }

      const code = (result.body as { error?: { code?: string } } | undefined)?.error?.code;
      const target = errorTarget(code, result.status);

      if (target.field === 'form') setFormError(t(target.key));
      else setErrors({ ...NO_ERRORS, [target.field]: t(target.key) });
    } catch {
      setFormError(t('errors.generic'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      onClose={close}
      size="lg"
      title={t('title')}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={close} disabled={submitting}>
            {t('back-cta')}
          </Button>
          {organizations !== null && (
            <Button type="submit" form="member-access-form" disabled={submitting}>
              {submitting ? t('submit-loading') : t('submit-cta')}
            </Button>
          )}
        </>
      }
    >
      <form id="member-access-form" className="space-y-4" onSubmit={submit} noValidate>
        <div className="space-y-1">
          <Label htmlFor="access-workspace-role">{t('workspace-role-label')}</Label>
          <NativeSelect
            id="access-workspace-role"
            value={workspaceRole}
            onChange={(value) => {
              setWorkspaceRole(value as WorkspaceRoleSlug | '');
              clearError('workspace-role');
            }}
            disabled={submitting}
            invalid={errors['workspace-role'] !== null}
            options={[
              { value: '', label: t('workspace-role-none') },
              ...WORKSPACE_ROLES.map((role) => ({ value: role, label: tRoles(role) })),
            ]}
          />
          {errors['workspace-role'] !== null && <p className="text-sm text-danger-600">{errors['workspace-role']}</p>}
        </div>

        {organizations === null ? (
          <p className="text-sm text-danger-600">{t('organizations-load-error')}</p>
        ) : (
          <div className="space-y-1">
            <fieldset className="space-y-3">
              <legend className="text-sm font-medium text-text-primary">{t('organizations-label')}</legend>
              <p className="text-sm text-text-tertiary">{t('organizations-hint')}</p>
              {organizations.map((organization) => (
                <fieldset key={organization.id} className="space-y-2">
                  <legend className="text-sm text-text-primary">
                    {organization.name}
                    {organization.type === 'main' && <span className="text-text-tertiary"> · {t('main-suffix')}</span>}
                  </legend>
                  <div className="flex flex-wrap gap-x-4 gap-y-2">
                    {ORG_ROLES.map((role) => (
                      <label key={role} className="flex items-center gap-2 text-sm text-text-primary">
                        <Checkbox
                          checked={rolesByStandort.get(organization.id)?.includes(role) ?? false}
                          disabled={submitting}
                          onCheckedChange={() => toggleRole(organization.id, role)}
                        />
                        {tRoles(role)}
                      </label>
                    ))}
                  </div>
                </fieldset>
              ))}
            </fieldset>
            {errors.organizations !== null && <p className="text-sm text-danger-600">{errors.organizations}</p>}
          </div>
        )}

        <div className="space-y-1">
          <Label htmlFor="access-primary">{t('primary-label')}</Label>
          <NativeSelect
            id="access-primary"
            value={primary ?? ''}
            placeholder={t('primary-none')}
            onChange={(value) => {
              setPrimaryPick(value);
              clearError('primary');
            }}
            disabled={submitting || assigned.length === 0}
            invalid={errors.primary !== null}
            options={assigned.map((o) => ({ value: o.id, label: o.name }))}
          />
          {errors.primary !== null && <p className="text-sm text-danger-600">{errors.primary}</p>}
        </div>

        {formError !== null && <p className="text-base text-danger-600">{formError}</p>}
      </form>
    </Modal>
  );
}
